import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { RefreshCw, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

const STATUS_COLORS = {
  Valid: 'bg-green-100 text-green-800',
  Invalid: 'bg-red-100 text-red-800',
  Cancelled: 'bg-gray-100 text-gray-600',
};

export const ETAReceived = () => {
  const { getAuthHeader } = useAuth();
  const { t, language } = useLanguage();
  const fileInputRef = useRef(null);

  const [invoices, setInvoices] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [syncFrom, setSyncFrom] = useState(daysAgo(30));
  const [syncTo, setSyncTo] = useState(today());

  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  const fetchInvoices = async () => {
    try {
      const params = {};
      if (filterFrom) params.date_from = filterFrom;
      if (filterTo) params.date_to = filterTo;
      const res = await axios.get(`${API}/eta/received`, { headers: getAuthHeader(), params });
      setInvoices(res.data || []);
    } catch {
      toast.error('Failed to fetch received invoices');
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [filterFrom, filterTo]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/eta/sync-received`, {
        date_from: syncFrom,
        date_to: syncTo,
      }, { headers: getAuthHeader() });
      toast.success(`${t('etaSyncSuccess')} — ${res.data.new} new invoice${res.data.new !== 1 ? 's' : ''} fetched`);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const formData = new FormData();
      for (const file of files) formData.append('files', file);
      const res = await axios.post(`${API}/eta/upload-json`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      const { imported, skipped } = res.data;
      toast.success(`Imported ${imported} invoice${imported !== 1 ? 's' : ''}${skipped > 0 ? ` (${skipped} skipped)` : ''}`);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString();
  };

  const statusLabel = (status) => {
    if (!status) return '—';
    const key = `etaStatus${status.charAt(0).toUpperCase()}${status.slice(1).toLowerCase()}`;
    return t(key) || status;
  };

  const ar = language === 'ar';

  return (
    <Card className="p-6 border border-border space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold" lang={ar ? 'ar' : 'en'}>{t('etaReceived')}</h2>
        <div className="flex gap-2">
          <input
            type="file"
            accept=".json"
            multiple
            ref={fileInputRef}
            onChange={handleFileUpload}
            className="hidden"
          />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} variant="outline" className="gap-2">
            <Upload className="w-4 h-4" />
            <span>{uploading ? (ar ? 'جارٍ الرفع...' : 'Uploading...') : (ar ? 'رفع ملفات JSON' : 'Upload JSON Files')}</span>
          </Button>
        </div>
      </div>

      {/* Sync panel */}
      <div className="p-4 bg-muted/30 rounded-md border border-border space-y-3">
        <p className="text-sm font-medium" lang={ar ? 'ar' : 'en'}>{t('syncFromETA')}</p>
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <Label className="text-xs text-muted-foreground">{t('from')}</Label>
            <Input type="date" value={syncFrom} onChange={e => setSyncFrom(e.target.value)} className="w-40" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{t('to')}</Label>
            <Input type="date" value={syncTo} onChange={e => setSyncTo(e.target.value)} className="w-40" />
          </div>
          <Button onClick={handleSync} disabled={syncing} className="gap-2 bg-accent hover:bg-accent/90">
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            <span lang={ar ? 'ar' : 'en'}>{syncing ? t('syncing') : t('syncFromETA')}</span>
          </Button>
        </div>
      </div>

      {/* Display filter */}
      <div className="flex flex-wrap gap-4 items-end">
        <div>
          <Label className="text-xs text-muted-foreground">{t('from')}</Label>
          <Input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} className="w-40" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">{t('to')}</Label>
          <Input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} className="w-40" />
        </div>
        {(filterFrom || filterTo) && (
          <Button variant="ghost" size="sm" onClick={() => { setFilterFrom(''); setFilterTo(''); }} className="text-muted-foreground">
            {t('clear')}
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-muted/50">
            <tr>
              <th className="border border-border p-3 text-start font-semibold">{t('date')}</th>
              <th className="border border-border p-3 text-start font-semibold">{t('issuer')}</th>
              <th className="border border-border p-3 text-start font-semibold">Invoice #</th>
              <th className="border border-border p-3 text-start font-semibold">{t('invoiceType')}</th>
              <th className="border border-border p-3 text-end font-semibold">{t('total')}</th>
              <th className="border border-border p-3 text-center font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv, idx) => (
              <tr key={idx} className="hover:bg-muted/30">
                <td className="border border-border p-3">{formatDate(inv.date_issued)}</td>
                <td className="border border-border p-3">
                  <div className="font-medium">{inv.issuer_name || '—'}</div>
                  {inv.issuer_id && <div className="text-xs text-muted-foreground">{inv.issuer_id}</div>}
                </td>
                <td className="border border-border p-3 font-mono text-xs">{inv.internal_id || '—'}</td>
                <td className="border border-border p-3 text-xs">{inv.type_name || '—'}</td>
                <td className="border border-border p-3 text-end font-mono-numeric font-semibold">
                  {inv.total != null ? inv.total.toFixed(2) : '—'} {t('currency')}
                </td>
                <td className="border border-border p-3 text-center">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_COLORS[inv.status] || 'bg-muted text-muted-foreground'}`}>
                    {statusLabel(inv.status)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {invoices.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <p>{t('noEntriesYet')}</p>
            <p className="text-xs mt-1">{ar ? 'حمّل ملفات JSON من بوابة ETA أو استخدم المزامنة' : 'Upload JSON files from the ETA portal or use sync above'}</p>
          </div>
        )}
      </div>
    </Card>
  );
};
